import {Modal} from 'react-bootstrap'
import {useSearchParams} from 'react-router-dom'
import {useQuery, useQueryClient} from 'react-query'
import {useState} from 'react'
import Swal from 'sweetalert2'
import {
  ContractAiResult,
  ContractSuggestedItem,
} from '../../../../app/modules/contracts/components/contracts-list/core/_models'
import {
  createTasksFromContractSuggestions,
  getAiAnalysisStatus,
} from '../../../../app/modules/contracts/components/contracts-list/core/_requests'
import {ContractSuggestedItemsPreview} from './ContractSuggestedItemsPreview'
import {getContractAiStatusMessage} from './contractAiMessages'
import {getApiErrorMessage, ID} from '../../../helpers'
import {invalidateContractsQueries} from '../../../../app/modules/contracts/components/contracts-list/core/invalidateContractsQueries'
import {Loading} from '../../components/loading/Loading'
import {Toast} from '../../widgets'

const PARAM = 'contractAiSuggestions'

function initializeSuggestedItems(items: ContractSuggestedItem[]): ContractSuggestedItem[] {
  return items.map((item) => ({
    ...item,
    is_task: true,
    is_phase: false,
  }))
}

function ContractAiSuggestionsModal() {
  const [searchParams, setSearchParams] = useSearchParams()
  const contractId = searchParams.get(PARAM) as ID | null
  const queryClient = useQueryClient()
  const [suggestedItems, setSuggestedItems] = useState<ContractSuggestedItem[]>([])
  const [isCreating, setIsCreating] = useState(false)

  const {data, isLoading, isError} = useQuery(
    ['contractAiAnalysis', contractId],
    () => getAiAnalysisStatus(contractId!),
    {
      enabled: !!contractId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const items = response.result?.suggestions?.items ?? []
        if (response.status === 'completed' && items.length > 0) {
          setSuggestedItems(initializeSuggestedItems(items))
        } else {
          setSuggestedItems([])
        }
      },
    }
  )

  const handleClose = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete(PARAM)
    setSearchParams(newParams)
    setSuggestedItems([])
  }

  const getSwalTheme = () => {
    const theme = window.localStorage.getItem('kt_theme_mode_value') || ''
    return theme === 'dark' ? '#353b48' : '#fff'
  }

  const handleConfirm = async (selected: ContractSuggestedItem[]) => {
    if (!contractId || selected.length === 0) return

    const tasksPayload = selected.map((i) => ({
      title: i.title,
      description: i.description ?? null,
      expected_date: i.expected_date ?? null,
    }))

    if (tasksPayload.length === 0) {
      Toast.fire({icon: 'warning', title: 'Selecione ao menos uma tarefa.'})
      return
    }

    setIsCreating(true)
    try {
      const r = await createTasksFromContractSuggestions(contractId, {tasks: tasksPayload})

      Swal.fire({
        icon: 'success',
        title: 'Tarefas criadas',
        html: `<h5>${r.message}</h5>`,
        background: getSwalTheme(),
        confirmButtonColor: '#009ef7',
        color: '#fff',
      })
      invalidateContractsQueries(queryClient)
      handleClose()
    } catch (ex) {
      Toast.fire({icon: 'error', title: getApiErrorMessage(ex)})
    } finally {
      setIsCreating(false)
    }
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className='text-center py-10'>
          <Loading />
          <p className='text-muted mt-4'>Carregando análise...</p>
        </div>
      )
    }

    if (isError || !data) {
      return <p className='text-muted'>Não foi possível carregar a análise deste contrato.</p>
    }

    if (data.status === 'queued' || data.status === 'processing') {
      return (
        <div className='text-center py-8'>
          <div className='spinner-border text-primary mb-4' />
          <p className='fw-semibold mb-1'>RushMind em andamento</p>
          <p className='text-muted mb-0'>
            O contrato foi salvo e o RushMind ainda está processando o documento. Você receberá uma
            notificação quando terminar.
          </p>
        </div>
      )
    }

    const result: ContractAiResult | undefined = data.result
    if (!result) {
      return <p className='text-muted'>Resultado da análise indisponível.</p>
    }

    const items = result.suggestions?.items ?? []
    const message = getContractAiStatusMessage(result.status, result.message)

    if (result.status === 'success' && items.length > 0) {
      return (
        <>
          <p className='text-gray-600 mb-3'>{message}</p>
          <ContractSuggestedItemsPreview
            items={suggestedItems.length > 0 ? suggestedItems : initializeSuggestedItems(items)}
            isCreating={isCreating}
            onConfirm={handleConfirm}
            onCancel={handleClose}
          />
        </>
      )
    }

    return (
      <div className='py-6'>
        <p className='text-gray-800 mb-2'>{message}</p>
        <button type='button' className='btn btn-light' onClick={handleClose}>
          Fechar
        </button>
      </div>
    )
  }

  return (
    <Modal
      show={!!contractId}
      onHide={handleClose}
      backdrop='static'
      dialogClassName='modal-dialog modal-dialog-centered modal-lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>Sugestões do RushMind — Contrato</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
    </Modal>
  )
}

export {ContractAiSuggestionsModal, PARAM as CONTRACT_AI_SUGGESTIONS_PARAM}
